import { defineDocument, link, list, p, section, strong, table, text, } from '../../schema';
export const report202602TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-02-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-02-top-party-games-and-streamers',
    route: '/party-central/reports/2026-02-top-party-games-and-streamers',
    seo: {
        title: 'February 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for February 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'February 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'violet',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-03-03T00:00:00.000Z'),
        updatedAt: new Date('2026-03-03T00:00:00.000Z'),
        reportMonth: '2026-02',
        dataRange: {
            start: '2026-02-12',
            end: '2026-02-28',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('February 2026 marks the inaugural month of Airhead Games streaming intelligence. Across a 17-day capture window (February 12–28), 114 capture events yielded 126,155 stream observations spanning 804 games, 55,047 unique streamers, and 34 languages. These figures represent approximately half of the expected monthly volume, as data collection began mid-month. Despite the compressed window, clear structural patterns emerge in game retention, audience concentration, and language distribution. See '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' for scoring and collection details.'),
                ]),
                p([
                    text('In our first ever Party Score standings, '),
                    strong('Diablo II: Resurrected'),
                    text(' lead with a score of 96. Released in 2021, it is still putting up impressive numbers five years later: 24,236 streams from 5,045 unique streamers, paired with a 4.8 stickiness that signals genuine community investment rather than casual sampling. At the opposite end of the spectrum, '),
                    strong('Words On Stream'),
                    text(' earned a 93 with just a modest 451 streamers, propelled by a stickiness of 9.2, the highest of any title. That figure means each streamer averaged over nine sessions in 17 days, a retention rate most game developers would envy.'),
                ]),
                p([
                    strong('Super Battle Golf'),
                    text(' is the definitive breakout of February. Launching on the 19th, it attracted 4,074 unique streamers and averaged 73 viewers per stream within 10 days, placing it third in the widest-streamer-base rankings ahead of established titles like Fall Guys and Lethal Company despite being available for only the last 10 days of the month.'),
                ]),
                p([
                    link('https://twitch.tv/gaules', 'Gaules'),
                    text(' earned the highest streamer Party Score of the month at 99, reflecting a rare convergence of consistency (27 captures), audience scale (1,298 average viewers), and genre breadth (nine distinct party games). No other streamer in the dataset combined all three dimensions at this level. For context, '),
                    link('https://twitch.tv/caseoh_', 'caseoh_'),
                    text(' drew 64,907 average viewers but appeared in only three captures of a single game, illustrating how party games frequently serve as intermission content for large variety channels rather than a primary focus.'),
                ]),
                p('English accounted for 64% of all streams. The remaining 36% was spread across 33 other languages, with 10 languages exceeding 400 streams each. Notably, Polish streams averaged 88 viewers per stream, four times the English average of 22, pointing to smaller but significantly more concentrated audiences in several non-English communities.'),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            blocks: [
                p([
                    text('Party Score is a proprietary metric developed by '),
                    link('/', 'Airhead Games'),
                    text(' to rank party game performance on Twitch. '),
                    strong('Game Party Score'),
                    text(" is a 0–100 percentile-ranked composite that measures a title's overall streaming presence by combining streamer breadth, streamer dedication (stickiness), and audience draw. "),
                    strong('Streamer Party Score'),
                    text(' uses a parallel methodology applied at the creator level, weighting how consistently a streamer appears, how large an audience they bring, and how many different party games they cover. Full scoring details are in the '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text('.'),
                ]),
            ],
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p('The ranking reveals a tension between scale and stickiness. Diablo II and Super Smash lead through massive streamer bases (5,045 and 2,806 respectively), while niche titles like Words On Stream and Pinball land on the list due to the strength of committed repeat streamers (stickiness of 9.2 and 3.2, respectively). Established party game staples cluster at a notably lower stickiness band: Among Us (1.5), Fall Guys (1.9), and Jackbox Party Packs (1.8) all fall below 2.0. This translates to roughly one session per week over the observation window, a pattern consistent with a weekly "community day" cadence in which streamers rotate party games into their schedule for audience participation rather than streaming them daily. Whether this once-a-week signal persists in a full month of data is a trend worth tracking.'),
                        p("Two genre clusters are worth noting. Nintendo titles claim four of the top 15 spots: Super Smash Bros. Ultimate (#2), Mario Kart World (#5), Super Mario Party Jamboree (#8), and Mario Kart 64 (#12). The latter two reach the rankings primarily through audience draw rather than volume, with Super Mario Party Jamboree averaging 99 viewers per stream and Mario Kart 64 averaging 49. Mahjong also places two titles, Mahjong Soul (#6, 1,121 streamers) and Amatsuki Mahjong (#11, 76 streamers), consistent with mahjong's rising global profile on streaming platforms over the past year, particularly across Japanese and Chinese communities."),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    'Diablo II: Resurrected',
                                    '96',
                                    '24,236',
                                    '5,045',
                                    '4.8',
                                    '35',
                                ],
                                [
                                    '2',
                                    'Super Smash Bros. Ultimate',
                                    '94',
                                    '7,714',
                                    '2,806',
                                    '2.7',
                                    '30',
                                ],
                                ['3', 'Words On Stream', '93', '4,141', '451', '9.2', '16'],
                                ['4', 'Fall Guys', '91', '7,199', '3,787', '1.9', '40'],
                                [
                                    '5',
                                    'Mario Kart World',
                                    '91',
                                    '3,689',
                                    '1,673',
                                    '2.2',
                                    '18',
                                ],
                                ['6', 'Mahjong Soul', '91', '2,371', '1,121', '2.1', '23'],
                                [
                                    '7',
                                    'Sonic Racing: CrossWorlds',
                                    '90',
                                    '4,173',
                                    '1,972',
                                    '2.1',
                                    '15',
                                ],
                                [
                                    '8',
                                    'Super Mario Party Jamboree',
                                    '90',
                                    '788',
                                    '500',
                                    '1.6',
                                    '99',
                                ],
                                [
                                    '9',
                                    'Just Dance 2026 Edition',
                                    '90',
                                    '305',
                                    '156',
                                    '2.0',
                                    '62',
                                ],
                                ['10', 'Pinball', '90', '431', '136', '3.2', '17'],
                                ['11', 'Amatsuki Mahjong', '90', '171', '76', '2.3', '42'],
                                ['12', 'Mario Kart 64', '90', '148', '67', '2.2', '49'],
                                ['13', 'Among Us', '89', '9,165', '6,019', '1.5', '39'],
                                ['14', 'Mafia Online', '89', '124', '23', '5.4', '38'],
                                [
                                    '15',
                                    'Jackbox Party Packs',
                                    '88',
                                    '2,385',
                                    '1,357',
                                    '1.8',
                                    '22',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The streamer rankings surface multiple pathways to a high Party Score. '),
                            link('https://twitch.tv/gaules', 'Gaules'),
                            text(' leads on the rare combination of consistency (27 captures), scale (1,298 average viewers), and breadth (nine games). His background is telling: a former professional Counter-Strike player for nearly a decade, Gaules now streams daily and has channeled that competitive discipline into party game variety. Volume anchors like '),
                            link('https://twitch.tv/cpxtrey', 'CPxTrey'),
                            text(' (75 captures across three games) contrast with high-reach channels like '),
                            link('https://twitch.tv/chilledchaos', 'ChilledChaos'),
                            text(' (2,856 average viewers) and '),
                            link('https://twitch.tv/sykkuno', 'Sykkuno'),
                            text(' (1,735 average viewers). Multi-title explorers such as '),
                            link('https://twitch.tv/astarriapple', 'AstarriApple'),
                            text(', a British voice actor, and '),
                            link('https://twitch.tv/getonmylvltv', 'GetOnMyLvLTV'),
                            text(' demonstrate that sustained genre engagement, not just audience size, can drive competitive scores.'),
                        ]),
                        p([
                            text('Scanning the bios of the top 15 adds a qualitative layer the numbers miss. These are not Twitch-native grinders; many built audiences elsewhere first. '),
                            link('https://twitch.tv/francesconigris', 'francesconigris'),
                            text(' (#12) converted a TikTok following, '),
                            link('https://twitch.tv/junkyard129', 'Junkyard129'),
                            text(' (#8) spent a decade on YouTube, and '),
                            link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                            text(' (#4) describes herself as a "Scientist/gamer." '),
                            link('https://twitch.tv/logan_usa', 'logan_usa'),
                            text(' (#14) carries competitive authority as the #1 ranked Mario Kart Wii time trialer. The unifying thread, however, is community: the top scorers overwhelmingly describe their channels around shared experience rather than personal skill, emphasizing playing together, building ritual, and showing up daily. It is a profile that maps neatly onto the consistency and breadth that Party Score is designed to reward.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/gaules', 'Gaules')],
                                    '99',
                                    '27',
                                    '1,298',
                                    '9',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/qttsix', '六希夫')],
                                    '97',
                                    '21',
                                    '1,476',
                                    '2',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '97',
                                    '27',
                                    '780',
                                    '2',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '97',
                                    '13',
                                    '289',
                                    '5',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '96',
                                    '16',
                                    '142',
                                    '6',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/chilledchaos', 'ChilledChaos')],
                                    '95',
                                    '7',
                                    '2,856',
                                    '4',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/choke7', 'Choke7')],
                                    '95',
                                    '22',
                                    '132',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/junkyard129', 'Junkyard129')],
                                    '95',
                                    '12',
                                    '121',
                                    '3',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/bellabiever', 'bellabiever')],
                                    '95',
                                    '26',
                                    '114',
                                    '2',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                    '95',
                                    '75',
                                    '65',
                                    '3',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/sykkuno', 'Sykkuno')],
                                    '94',
                                    '7',
                                    '1,735',
                                    '3',
                                ],
                                [
                                    '12',
                                    [
                                        link('https://twitch.tv/francesconigris', 'francesconigris'),
                                    ],
                                    '94',
                                    '9',
                                    '144',
                                    '3',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/calliemacn', 'CallieMacN')],
                                    '94',
                                    '16',
                                    '97',
                                    '2',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/logan_usa', 'logan_usa')],
                                    '94',
                                    '18',
                                    '96',
                                    '2',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/getonmylvltv', 'GetOnMyLvLTV')],
                                    '94',
                                    '11',
                                    '96',
                                    '6',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            blocks: [
                p('The tables below rank games four ways: most streamed, biggest audiences, widest streamer base, and stickiest. Each ranking tells a different story about how a title earns attention.'),
            ],
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p('Diablo II: Resurrected leads comfortably with more than 2.5x the streams of the next title. The standout is Super Battle Golf at #5: a recent release that already commands the highest average viewership in the top 10, suggesting strong spectator appeal alongside rapid adoption. Lethal Company (#6) is the inverse, high stream volume paired with the lowest average viewers on the list, a profile of low-key co-op sessions that players enjoy but audiences largely skip.'),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Diablo II: Resurrected', '24,236', '5,045', '35'],
                                ['2', 'Among Us', '9,165', '6,019', '39'],
                                ['3', 'Super Smash Bros. Ultimate', '7,714', '2,806', '30'],
                                ['4', 'Fall Guys', '7,199', '3,787', '40'],
                                ['5', 'Super Battle Golf', '5,133', '4,074', '73'],
                                ['6', 'Lethal Company', '4,628', '2,827', '7'],
                                ['7', 'Sonic Racing: CrossWorlds', '4,173', '1,972', '15'],
                                ['8', 'Words On Stream', '4,141', '451', '16'],
                                ['9', 'Mario Kart World', '3,689', '1,673', '18'],
                                ['10', 'Party Animals', '3,115', '1,656', '10'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('This table surfaces games the volume rankings miss entirely. Maitre des Fleurs, a French-language flower-arranging game prominently streamed by '),
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(" (this month's #2 French audience streamer), tops the list on the strength of a small number of high-profile broadcasts. Tentacle Tango and The You Quiz follow a similar pattern: tiny stream counts inflated by a handful of large creators. The more durable signal is further down the table, where LEGO Party! and Super Bunny Man sustain triple-digit average viewership across 100+ streams each, evidence that audience pull is not exclusively a small-sample artifact."),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Maitre des Fleurs', '734', '20', '10'],
                                ['2', 'Tentacle Tango', '676', '10', '8'],
                                ['3', 'The You Quiz', '567', '20', '20'],
                                ['4', 'Biped 2', '420', '54', '42'],
                                ['5', 'Casino', '303', '49', '27'],
                                ['6', 'Super Bunny Man', '225', '139', '129'],
                                ['7', 'Wii Party', '191', '37', '30'],
                                ['8', 'The Game of Life', '178', '23', '21'],
                                ['9', 'MicroWorks', '167', '47', '35'],
                                ['10', 'LEGO Party!', '163', '151', '129'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p('Among Us tops the breadth chart with nearly 1,000 more unique streamers than second-place Diablo II, making it the most broadly discovered party game on Twitch. Super Battle Golf and Fall Guys form a strong middle tier. Gartic Phone is a notable inclusion: it does not crack the top 10 in stream volume, yet attracts a wide variety of creators, the profile of a game people reach for occasionally rather than build a channel around.'),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'Among Us', '6,019', '9,165'],
                                ['2', 'Diablo II: Resurrected', '5,045', '24,236'],
                                ['3', 'Super Battle Golf', '4,074', '5,133'],
                                ['4', 'Fall Guys', '3,787', '7,199'],
                                ['5', 'Lethal Company', '2,827', '4,628'],
                                ['6', 'Super Smash Bros. Ultimate', '2,806', '7,714'],
                                ['7', 'Sonic Racing: CrossWorlds', '1,972', '4,173'],
                                ['8', 'Gartic Phone', '1,798', '2,803'],
                                ['9', 'Mario Kart World', '1,673', '3,689'],
                                ['10', 'Party Animals', '1,656', '3,115'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p('Stickiness isolates genuine retention from one-time curiosity. Ragnarok X: Next Generation and Words On Stream sit well above the field, with streamers averaging nine or more sessions each in 17 days. Three entries from The Legend of Heroes: Trails of Cold Steel series also appear, revealing a JRPG community pattern in which streamers commit to extended playthroughs rather than single-session sampling.'),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'Ragnarok X: Next Generation', '9.8', '590', '60'],
                                ['2', 'Words On Stream', '9.2', '4,141', '451'],
                                ['3', 'Super Monkey Ball 2', '5.8', '150', '26'],
                                ['4', 'Mafia Online', '5.4', '124', '23'],
                                [
                                    '5',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '5.1',
                                    '168',
                                    '33',
                                ],
                                ['6', 'Diablo II: Resurrected', '4.8', '24,236', '5,045'],
                                ['7', 'Dragon Ball: The Breakers', '4.6', '358', '77'],
                                [
                                    '8',
                                    'The Legend of Heroes: Trails of Cold Steel II',
                                    '4.6',
                                    '120',
                                    '26',
                                ],
                                [
                                    '9',
                                    'The Legend of Heroes: Trails of Cold Steel',
                                    '4.3',
                                    '185',
                                    '43',
                                ],
                                ['10', 'Placid Plastic Duck Simulator', '3.9', '758', '194'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            blocks: [
                p([
                    text('Twenty-five party games launched in February 2026. One broke out. '),
                    strong('Super Battle Golf'),
                    text(' accounted for virtually all new-release streaming activity, while 19 titles registered no Twitch presence at all.'),
                ]),
            ],
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            text("Super Battle Golf's dominance is not just relative; it is absolute. Launched February 19 by indie studio "),
                            link('https://brimstone.games/', 'Brimstone'),
                            text(', the game '),
                            link('https://store.steampowered.com/news/app/4069520/view/497221052658616493', 'sold 100,000 copies'),
                            text(' in its first 48 hours, '),
                            link('https://ixbt.games/en/news/2026/03/01/v-steam-novyi-indi-xit-400-tysiac-prodannyx-kopii-super-battle-golf-govoriat-sami-za-sebia.html', 'crossed 400,000'),
                            text(' by early March, and earned an '),
                            link('https://store.steampowered.com/app/4069520/Super_Battle_Golf/', '"Overwhelmingly Positive"'),
                            text(' rating on Steam. The game pits up to eight players against each other simultaneously with items like orbital lasers, rocket launchers, and land mines, the kind of unpredictable moments that clip well and spread organically on Twitch. As a result, we spotted more than 4,000 streamers playing it during our stream capture events, a number rivaling much more established titles in the party game genre.'),
                        ]),
                        p("The remaining February releases with Twitch activity tell a more typical story of quiet indie launches: single-digit stream counts and low stickiness while they haven't yet cracked regular game rotations."),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['Super Battle Golf', 'Feb 19', '5,133', '4,074', '73'],
                                ['Spider Roulette', 'Feb 23', '7', '7', '62'],
                                ['Choppy Cuts', 'Feb 4', '5', '4', '21'],
                                ['Ghost Gunners', 'Feb 6', '2', '2', '42'],
                                ['RV Drive There Co-op', 'Feb 5', '2', '2', '2'],
                                ['I Have an Item', 'Feb 20', '1', '1', '5'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('The remaining 19 party games launched in February saw no Twitch streaming activity during the capture window. We list them here in hopes of helping creators and audiences discover something new.'),
                        list({
                            items: [
                                'Damashi Game (Feb 1)',
                                'Pixel World (Feb 3)',
                                'Super Platform Tag (Feb 3)',
                                '4PGP (Feb 4)',
                                'Draw King (Feb 5)',
                                'Is this potato? (Feb 5)',
                                'Super Bomberman Collection (Feb 5)',
                                'Mimetic (Feb 6)',
                                'drawwars.io (Feb 10)',
                                'Find the Four-Leaf Clover (Feb 12)',
                                'Bud Masters: Battle Edition (Feb 13)',
                                'Sorted (Feb 13)',
                                'Insert Coin: Arcade Party (Feb 14)',
                                'DisasterPR+ (Feb 15)',
                                'Boiiing Boiiing (Feb 17)',
                                'Qubic 2025 Bestsellers (Feb 21)',
                                'Royal Vermin (Feb 23)',
                                'Color Mismatch Brain Quiz (Feb 26)',
                                'Top Cop: Police Training (Feb 28)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            blocks: [
                p('This section profiles streamers three ways: most active, biggest audiences, and most versatile. Each surfaces a different type of creator influence in the party game ecosystem.'),
            ],
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Seven of the top 10 most active streamers played only one game, and the top of the chart is largely powered by dedicated broadcast channels rather than live variety streamers. '),
                            link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                            text(' restreams Super Smash Bros. Ultimate tournaments, '),
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' runs 24/7 VOD reruns for Diablo II speedrunner MrLlamaSC, and '),
                            link('https://twitch.tv/suzyqpid', 'suzyqpid'),
                            text(' has built a loyalty-coin ecosystem around Words On Stream. The surprise is that rebroadcast and always-on formats still draw meaningful audiences in party game categories, a niche that keeps these titles and creators visible around the clock.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '122',
                                    '15',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '114',
                                    '110',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '114',
                                    '11',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '112',
                                    '17',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                    '109',
                                    '41',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '108',
                                    '32',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '88',
                                    '143',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                    '75',
                                    '65',
                                    '3',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/ss7even', 'Ss7even')],
                                    '75',
                                    '26',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/4cvit', '4CVIT')],
                                    '70',
                                    '136',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 captures',
                    collapsible: true,
                    blocks: [
                        p([
                            text('This chart is dominated by mega-streamers who dipped into party games briefly but carried massive followings with them. '),
                            link('https://twitch.tv/caseoh_', 'caseoh_'),
                            text(', who describes himself as "The dude from tik tok," and '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' each played a single party game across just a few sessions. Party games serve as palette-cleanser content for these channels: low-commitment, community-friendly, and easy to pick up between main titles. Even these fleeting crossover moments expose the category to tens of thousands of potential new viewers.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Captures',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                    '64,907',
                                    '3',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '37,923',
                                    '3',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                    '22,484',
                                    '3',
                                    '3',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/northernlion', 'Northernlion')],
                                    '12,284',
                                    '2',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/zerator', 'ZeratoR')],
                                    '11,768',
                                    '2',
                                    '2',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/youngmulti', 'YoungMulti')],
                                    '10,580',
                                    '2',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/genesisgg', 'genesisgg')],
                                    '10,422',
                                    '2',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/rdcgaming', 'RDCgaming')],
                                    '9,849',
                                    '3',
                                    '3',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                    ],
                                    '9,772',
                                    '4',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/shroud', 'shroud')],
                                    '7,919',
                                    '2',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Versatility is the rarest trait in party game streaming. Most streamers stick to one or two titles, making anyone covering seven or more a deliberate genre explorer. '),
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(', the top entry at 13 games, is actually a 24/7 automated channel that rotates CPU-vs-CPU matches across retro party games, a format that naturally inflates game count. '),
                            link('https://twitch.tv/gaules', 'Gaules'),
                            text(' is the only high-audience creator on this list, and the only one who combines broad game coverage with the consistency and viewership to meaningfully influence discovery across multiple titles.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Captures',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '13',
                                    '43',
                                    '20',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/gaules', 'Gaules')],
                                    '9',
                                    '27',
                                    '1,298',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/retrorecap', 'RETRORECAP')],
                                    '8',
                                    '14',
                                    '20',
                                ],
                                [
                                    '4',
                                    [
                                        link('https://twitch.tv/keltathepomeranian', 'KeltaThePomeranian'),
                                    ],
                                    '8',
                                    '8',
                                    '34',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/wardere', 'WARDERE')],
                                    '7',
                                    '30',
                                    '30',
                                ],
                                [
                                    '6',
                                    [
                                        link('https://twitch.tv/mrelectricnick', 'MrElectricNick'),
                                    ],
                                    '7',
                                    '8',
                                    '30',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/natethenoobtv', 'NatetheNoobTV')],
                                    '7',
                                    '7',
                                    '11',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/lorsen_1805', 'Lorsen_1805')],
                                    '6',
                                    '34',
                                    '22',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/lukealele', 'lukealele')],
                                    '6',
                                    '27',
                                    '30',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '6',
                                    '16',
                                    '142',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            blocks: [
                p('Party game streaming on Twitch spans 34 languages, with 10 exceeding 400 streams in the reporting window. English accounts for 64% of total volume, but the more interesting story is in the non-English communities, each of which has a distinct streaming culture and game preferences.'),
            ],
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p('A few smaller communities punch well above their weight in viewer density. Polish streams average four times the English per-stream viewership despite representing a fraction of the volume, driven by a concentrated creator base with outsized audiences. Chinese and Portuguese follow a similar pattern.'),
                        p('The per-language breakdowns below reveal genre preferences that cut along cultural lines. Mahjong is an Asian-language phenomenon: Mahjong Soul leads Japanese streaming and two mahjong titles appear in the Chinese top five. Just Dance draws audiences in Spanish, Polish, and Italian but is absent from English and Asian audience charts. Russian streaming stands out for its social game culture, with Gartic Phone, Jackbox, and SIGame all ranking in the top five. PICO PARK 2 is drawing some of the largest audiences in Russian and Portuguese but did not crack the global Games Drawing Biggest Audiences.'),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '80,197', '34,315', '22'],
                                ['2', 'Spanish', '8,830', '4,458', '13'],
                                ['3', 'German', '7,631', '3,595', '42'],
                                ['4', 'French', '6,487', '3,033', '39'],
                                ['5', 'Russian', '5,590', '2,362', '27'],
                                ['6', 'Japanese', '5,415', '2,326', '40'],
                                ['7', 'Portuguese', '2,941', '1,467', '42'],
                                ['8', 'Chinese', '2,912', '982', '55'],
                                ['9', 'Polish', '1,545', '520', '88'],
                                ['10', 'Italian', '870', '443', '21'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '14,617', '3,202'],
                                        ['2', 'Super Smash Bros. Ultimate', '5,266', '1,865'],
                                        ['3', 'Among Us', '5,040', '3,268'],
                                        ['4', 'Super Battle Golf', '4,314', '3,397'],
                                        ['5', 'Fall Guys', '4,073', '2,235'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'The You Quiz', '1,872', '6'],
                                        ['2', 'Casino', '383', '25'],
                                        ['3', 'LEGO Party!', '207', '114'],
                                        ['4', 'The Choicer Voicer', '206', '6'],
                                        ['5', 'Pokémon Sun', '191', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '122',
                                            '15',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '114',
                                            '110',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '114',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '112',
                                            '17',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '108',
                                            '32',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                            '64,907',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '12,284',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/genesisgg', 'genesisgg')],
                                            '10,422',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/rdcgaming', 'RDCgaming')],
                                            '9,849',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/shroud', 'shroud')],
                                            '7,919',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-2',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '1,075', '219'],
                                        ['2', 'Fall Guys', '931', '543'],
                                        ['3', 'Among Us', '908', '659'],
                                        ['4', 'Super Smash Bros. Ultimate', '861', '375'],
                                        ['5', 'Mario Kart World', '533', '215'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-2',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Wii Sports', '185', '7'],
                                        ['2', 'Just Dance 2026 Edition', '173', '48'],
                                        ['3', 'Dale & Dawson Stationery Supplies', '109', '6'],
                                        ['4', 'Super Mario Party Jamboree', '80', '62'],
                                        ['5', 'Keep Talking and Nobody Explodes', '31', '30'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-2',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mictiantv', 'mictiantv')],
                                            '44',
                                            '17',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrcarpatv', 'MrCarpaTV')],
                                            '36',
                                            '192',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/cahos_gaming', 'CAHOS_GAMING'),
                                            ],
                                            '35',
                                            '132',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/zemistoklestv', 'zemistoklestv'),
                                            ],
                                            '27',
                                            '12',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/siniestro_ok', 'Siniestro_Ok'),
                                            ],
                                            '26',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-2',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,381',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/nexxuz', 'Nexxuz')],
                                            '1,527',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '1,447',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/lawlergg', 'lawlergg')],
                                            '716',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/nythak', 'Nythak')],
                                            '516',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-3',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '1,918', '405'],
                                        ['2', 'Among Us', '1,246', '791'],
                                        ['3', 'Fall Guys', '557', '287'],
                                        ['4', 'Mario Kart 8 Deluxe', '331', '177'],
                                        ['5', 'Golf With Your Friends', '320', '219'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-3',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Among Us', '135', '1,246'],
                                        ['2', 'Words On Stream', '93', '24'],
                                        ['3', 'Placid Plastic Duck Simulator', '68', '16'],
                                        ['4', 'THE GAME OF LIFE 2', '58', '5'],
                                        ['5', 'PlateUp!', '52', '66'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-3',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miami_max', 'miami_max')],
                                            '45',
                                            '145',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/baskenater', 'Baskenater')],
                                            '37',
                                            '214',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/lorsen_1805', 'Lorsen_1805')],
                                            '34',
                                            '22',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/zan_koko', 'zan_koko')],
                                            '33',
                                            '26',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/chillamaggi', 'chillamaggi')],
                                            '32',
                                            '88',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-3',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '37,923',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shlorox', 'Shlorox')],
                                            '5,529',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '3,964',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/xthesolutiontv', 'xTheSolutionTV'),
                                            ],
                                            '3,888',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/honeypuu', 'HoneyPuu')],
                                            '1,890',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-4',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '931', '211'],
                                        ['2', 'Among Us', '858', '511'],
                                        ['3', 'Fall Guys', '637', '308'],
                                        ['4', 'Mario Kart World', '528', '231'],
                                        ['5', 'Super Smash Bros. Ultimate', '479', '188'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-4',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Maitre des Fleurs', '734', '20'],
                                        ['2', 'Paddle Paddle Paddle', '652', '14'],
                                        ['3', 'Super Mario Party Jamboree', '620', '59'],
                                        ['4', 'Super Battle Golf', '439', '120'],
                                        ['5', 'Quizei', '130', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-4',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/anas6424v1', 'anas6424v1')],
                                            '114',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/deusrhog', 'DeusRhog')],
                                            '93',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ss7even', 'Ss7even')],
                                            '75',
                                            '26',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/henip', 'henip')],
                                            '43',
                                            '170',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/jiizk', 'JIIZK')],
                                            '36',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-4',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/zerator', 'ZeratoR')],
                                            '11,768',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,926',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '3,872',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/bagherajones', 'BagheraJones'),
                                            ],
                                            '2,170',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/alderiate', 'Alderiate')],
                                            '1,956',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-5',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '1,513', '301'],
                                        ['2', 'Gartic Phone', '681', '399'],
                                        ['3', 'Jackbox Party Packs', '410', '167'],
                                        ['4', 'Lethal Company', '405', '156'],
                                        ['5', 'SIGame', '254', '193'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-5',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'PICO PARK 2', '639', '11'],
                                        ['2', 'Casino', '360', '11'],
                                        ['3', 'The House Always Wins', '325', '7'],
                                        ['4', 'Mario Kart World', '242', '10'],
                                        ['5', 'LEGO Party!', '161', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-5',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '109',
                                            '41',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/jimjamich', 'jimjamich')],
                                            '57',
                                            '17',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/sashatechnic', 'SashaTechnic'),
                                            ],
                                            '45',
                                            '26',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kasto999', 'Kasto999')],
                                            '41',
                                            '44',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/hc_fm', 'hc_fm')],
                                            '37',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-5',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/olesha', 'Olesha')],
                                            '2,312',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tabula', 'Tabula')],
                                            '1,172',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/karmikkoala', 'KarmikKoala')],
                                            '1,096',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/pathofexilebota', 'pathofexilebota'),
                                            ],
                                            '1,062',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/wildkitten999', 'wildkitten999'),
                                            ],
                                            '887',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-6',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '1,052', '481'],
                                        ['2', 'Super Smash Bros. Ultimate', '694', '192'],
                                        ['3', 'Beat Saber', '422', '136'],
                                        ['4', 'Mario Kart World', '333', '107'],
                                        ['5', 'Among Us', '251', '194'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-6',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Biped 2', '1,871', '12'],
                                        ['2', 'Super Bunny Man', '479', '61'],
                                        ['3', 'Sonic Racing: CrossWorlds', '358', '95'],
                                        ['4', 'Lateral Thinking Together', '223', '5'],
                                        ['5', 'Super Smash Bros. Ultimate', '123', '694'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-6',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nohoho12', 'のほほ')],
                                            '27',
                                            '780',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/pikarium_', 'ぴかり')],
                                            '27',
                                            '38',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/pokapokacan', 'ぽかぽかさん')],
                                            '24',
                                            '30',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/garai_jls', 'がらい')],
                                            '21',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/atatulmkw', 'atatulmkw')],
                                            '19',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-6',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                            '22,484',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                            ],
                                            '9,772',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/alfrea', 'らいじん')],
                                            '1,034',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nohoho12', 'のほほ')],
                                            '780',
                                            '27',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/gackt_n', 'gackt_N')],
                                            '652',
                                            '13',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-7',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '548', '140'],
                                        ['2', 'Fall Guys', '222', '85'],
                                        ['3', 'Words On Stream', '175', '36'],
                                        ['4', 'Stumble Guys', '173', '76'],
                                        ['5', 'Super Battle Golf', '155', '115'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-7',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'MicroWorks', '634', '12'],
                                        ['2', 'PICO PARK 2', '276', '5'],
                                        ['3', 'Pummel Party', '239', '92'],
                                        ['4', 'Super Battle Golf', '225', '155'],
                                        ['5', 'Dale & Dawson Stationery Supplies', '121', '11'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-7',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '88',
                                            '143',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/partyanimalsbr', 'partyanimalsbr'),
                                            ],
                                            '36',
                                            '54',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/heylel_g', 'Heylel_G')],
                                            '36',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/albatrox18', 'albatrox18')],
                                            '29',
                                            '166',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '27',
                                            '1,298',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-7',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '1,298',
                                            '27',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/rnakano', 'RNakano')],
                                            '727',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/liminhag0d', 'liminhag0d')],
                                            '576',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bt0tv', 'bt0tv')],
                                            '358',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mano_imp', 'Mano_Imp')],
                                            '219',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-8',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '963', '126'],
                                        ['2', 'Mahjong Soul', '715', '359'],
                                        ['3', 'Party Animals', '211', '81'],
                                        ['4', 'Goose Goose Duck', '163', '127'],
                                        ['5', 'Amatsuki Mahjong', '155', '63'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-8',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Jinsei Game for Nintendo Switch', '781', '5'],
                                        ['2', 'Super Battle Golf', '185', '61'],
                                        ['3', 'Goose Goose Duck', '123', '163'],
                                        ['4', 'Golf With Your Friends', '102', '9'],
                                        ['5', 'Diablo II: Resurrected', '62', '963'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-8',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/rainpearmb', 'RainPearMB')],
                                            '50',
                                            '22',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ssqrush', 'SsQRush')],
                                            '34',
                                            '355',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/purplecat97803', '紫の喵')],
                                            '31',
                                            '43',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dawhan6260', '幻幻幻')],
                                            '30',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/duchycool', '信義虎')],
                                            '27',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-8',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mobilmobil', '老皮')],
                                            '5,896',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bbbb87', '世誠')],
                                            '4,104',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/hennie2001', '烟花蹦蹦蹦')],
                                            '1,752',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/qttsix', '六希夫')],
                                            '1,476',
                                            '21',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/liang_0714', '小亮')],
                                            '1,258',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-9',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '981', '169'],
                                        ['2', 'Among Us', '123', '76'],
                                        ['3', 'Fall Guys', '65', '21'],
                                        ['4', 'Gartic Phone', '47', '37'],
                                        ['5', 'Pummel Party', '29', '27'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-9',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '485', '19'],
                                        ['2', 'Among Us', '444', '123'],
                                        ['3', 'Gartic Phone', '199', '47'],
                                        ['4', 'Codenames', '147', '11'],
                                        ['5', 'Just Dance 2026 Edition', '110', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-9',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '55',
                                            '293',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/czechoooos', 'czechoooos')],
                                            '46',
                                            '41',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kondyss', 'Kondyss')],
                                            '23',
                                            '105',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '23',
                                            '25',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/fifus92', 'Fifus92')],
                                            '19',
                                            '24',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-9',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/youngmulti', 'YoungMulti')],
                                            '10,580',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ewroon', 'EWROON')],
                                            '7,884',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrdzinold', 'MrDzinold')],
                                            '2,411',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nexe_', 'Nexe_')],
                                            '1,691',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/graf', 'Graf')],
                                            '1,116',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-10',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '129', '53'],
                                        ['2', 'Among Us', '115', '73'],
                                        ['3', 'Diablo II: Resurrected', '98', '35'],
                                        ['4', 'Mario Kart World', '59', '19'],
                                        ['5', 'Super Smash Bros. Ultimate', '46', '23'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-10',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '265', '8'],
                                        ['2', 'Make it Meme', '132', '9'],
                                        ['3', 'Among Us', '52', '115'],
                                        ['4', 'Just Dance 2026 Edition', '50', '10'],
                                        ['5', 'Mario Kart Wii', '33', '21'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-10',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/bezttopeu', 'BeztTopEU')],
                                            '16',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '11',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/exandertv', 'exandertv')],
                                            '11',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/sickbruno', 'sickBruno')],
                                            '10',
                                            '11',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '9',
                                            '144',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-10',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/teknoyd', 'teknoyd')],
                                            '1,228',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/andreadel1988', 'andreadel1988'),
                                            ],
                                            '486',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tuberanza', 'Tuberanza')],
                                            '374',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mollu', 'Mollu')],
                                            '351',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '144',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            blocks: [
                p('The following deep dives profile the top 10 games by Party Score, detailing streamer-level engagement for each title.'),
                p([
                    text("Cross-referencing the tables reveals a clear divide between game-level stars and ecosystem-level stars. No streamer appears in more than one game's top 5; each deep dive is populated by game-specific specialists. Meanwhile, only two of the top 15 Party Score streamers appear in any deep dive at all: "),
                    link('https://twitch.tv/gaules', 'Gaules'),
                    text(' (#1 Party Score, #2 Fall Guys audience) and '),
                    link('https://twitch.tv/logan_usa', 'logan_usa'),
                    text(' (#14 Party Score, #5 Mario Kart World most active).'),
                ]),
                p('Top Pinball streamers appear nowhere else in the report, indicating a tight-knit and dedicated community. That stickiness helped push Pinball into the top 10 games by Party Score.'),
            ],
            sections: [
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('24,236 streams | 5,045 streamers | 4.8 stickiness | 35 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '114',
                                            '110',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '60',
                                            '39',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '55',
                                            '293',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/d2hustlersofficial', 'D2HustlersOfficial'),
                                            ],
                                            '54',
                                            '22',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/datmodz', 'DatModz')],
                                            '53',
                                            '1,644',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/peter', 'Peter')],
                                            '3,828',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '2,151',
                                            '42',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/alderiate', 'Alderiate')],
                                            '1,956',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/datmodz', 'DatModz')],
                                            '1,644',
                                            '53',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/nl_kripp', 'nl_Kripp')],
                                            '1,563',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('7,714 streams | 2,806 streamers | 2.7 stickiness | 30 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-2',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '122',
                                            '15',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/afk_host', 'AFK_Host')],
                                            '107',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/amiibots', 'Amiibots')],
                                            '106',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mew2king247', 'Mew2King247')],
                                            '50',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/dragmireqc', 'DragmireQc')],
                                            '30',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-2',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/genesisgg', 'genesisgg')],
                                            '10,422',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                            ],
                                            '9,772',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '3,872',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/genesisgg_2', 'genesisgg_2')],
                                            '2,273',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/coney', 'CONEY')],
                                            '1,568',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('4,141 streams | 451 streamers | 9.2 stickiness | 16 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-3',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '114',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '112',
                                            '17',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '108',
                                            '32',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '85',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/4cvit', '4CVIT')],
                                            '70',
                                            '136',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-3',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eastahh', 'Eastahh')],
                                            '458',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/emilycc', 'emilycc')],
                                            '268',
                                            '44',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/sandyjackson', 'sandyjackson'),
                                            ],
                                            '184',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/xem92', 'Xem92')],
                                            '138',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/4cvit', '4CVIT')],
                                            '136',
                                            '70',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__fall-guys',
                    title: 'Fall Guys',
                    collapsible: true,
                    blocks: [
                        p('7,199 streams | 3,787 streamers | 1.9 stickiness | 40 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-4',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '88',
                                            '143',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/hexagone____', 'Hexagone____'),
                                            ],
                                            '35',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/weedmanyogurt24', 'weedmanyogurt24'),
                                            ],
                                            '33',
                                            '16',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/jiizk', 'JIIZK')],
                                            '26',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sciezar', 'sciezar')],
                                            '26',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-4',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                            '64,907',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '922',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nythak', 'Nythak')],
                                            '602',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/streamwithlien', 'StreamwithLien'),
                                            ],
                                            '437',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/firedragon', 'firedragon')],
                                            '244',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-world',
                    title: 'Mario Kart World',
                    collapsible: true,
                    blocks: [
                        p('3,689 streams | 1,673 streamers | 2.2 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-5',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/vantox_', 'Vantox_')],
                                            '29',
                                            '69',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shy_guay', 'shy_guay')],
                                            '24',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nitthenat', 'NitTheNat')],
                                            '20',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/axel14_rk', 'axel14_rk')],
                                            '20',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/logan_usa', 'logan_usa')],
                                            '17',
                                            '98',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-5',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '911',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/terroriser', 'Terroriser')],
                                            '829',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/lllmokelll', 'もけっ')],
                                            '520',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kolderiu', 'Kolderiu')],
                                            '407',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/gamescage', 'GamesCage')],
                                            '403',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('2,371 streams | 1,121 streamers | 2.1 stickiness | 23 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-6',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '24',
                                            '28',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gelnarx', 'Gelnarx')],
                                            '23',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sudaouo333', '蘇蘇子')],
                                            '18',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/raisinnnnnn', 'raisinnnnnn')],
                                            '14',
                                            '13',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/beefmcmeat', 'BeefMcMeat')],
                                            '13',
                                            '20',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-6',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/p1lepe1el1', '珮蕾')],
                                            '1,242',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/liang_0714', '小亮')],
                                            '1,152',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/earendelxdfp', '厄倫蒂兒')],
                                            '914',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mayberuu', '琉花花_')],
                                            '282',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zenyahima', 'ZenyaHima')],
                                            '196',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__sonic-racing-crossworlds',
                    title: 'Sonic Racing: CrossWorlds',
                    collapsible: true,
                    blocks: [
                        p('4,173 streams | 1,972 streamers | 2.1 stickiness | 15 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-7',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tsuyuame', 'TsuyuAme')],
                                            '28',
                                            '15',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/jpofferz', 'Jpofferz')],
                                            '24',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/garomask_', 'garomask_')],
                                            '23',
                                            '16',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/forginal', 'Forginal')],
                                            '23',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/lunaiiiiiire', 'Lunaiiiiiire'),
                                            ],
                                            '21',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-7',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/alfrea', 'らいじん')],
                                            '1,034',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lythero', 'Lythero')],
                                            '740',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xababtv', 'XababTV')],
                                            '484',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/syaruru3', 'しゃるる')],
                                            '312',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/japanesekoreanug', 'JapaneseKoreanUG'),
                                            ],
                                            '169',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-mario-party-jamboree',
                    title: 'Super Mario Party Jamboree',
                    collapsible: true,
                    blocks: [
                        p('788 streams | 500 streamers | 1.6 stickiness | 99 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-8',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/wardere', 'WARDERE')],
                                            '9',
                                            '27',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/jordankvods', 'JordanKVODs')],
                                            '9',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ariesgenial', 'ariesgenial')],
                                            '6',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dersnatcha', 'DerSnatcha')],
                                            '4',
                                            '58',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/donpaulieone', 'DonPaulieone'),
                                            ],
                                            '4',
                                            '38',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-8',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/youngmulti', 'YoungMulti')],
                                            '10,580',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sayu', 'Sayu')],
                                            '512',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/dersnatcha', 'DerSnatcha')],
                                            '58',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/riff_tm', 'Riff_TM')],
                                            '47',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/galactic', 'Galactic')],
                                            '40',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('305 streams | 156 streamers | 2.0 stickiness | 62 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-9',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dellamaurie', 'dellAMAURIE')],
                                            '14',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ratatoscr', 'RATATOscr')],
                                            '11',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/floridawoman', 'floridawoman'),
                                            ],
                                            '7',
                                            '24',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ulises_eric', 'Ulises_Eric')],
                                            '7',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/nina_louise22', 'nina_louise22'),
                                            ],
                                            '6',
                                            '68',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-9',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,381',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,294',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/aileensenpai', 'AileenSenpai'),
                                            ],
                                            '351',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/sincerelylyn', 'sincerelyLyn'),
                                            ],
                                            '181',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sophelyy', 'Sophelyy')],
                                            '150',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('431 streams | 136 streamers | 3.2 stickiness | 17 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-10',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '17',
                                            '58',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/diehardpinball', 'diehardpinball'),
                                            ],
                                            '17',
                                            '54',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/laser_los', 'laser_los')],
                                            '12',
                                            '31',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/insanitypinball', 'InsanityPinball'),
                                            ],
                                            '10',
                                            '11',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/lazarusnoxpinball', 'lazarusnoxpinball'),
                                            ],
                                            '8',
                                            '39',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-10',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/aimlesspinball', 'AimlessPinball'),
                                            ],
                                            '247',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '92',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '58',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/electricbatarcade', 'ElectricBatArcade'),
                                            ],
                                            '58',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/diehardpinball', 'diehardpinball'),
                                            ],
                                            '54',
                                            '17',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            blocks: [
                p('Streaming activity follows two distinct rhythmic patterns: a weekly cycle driven by leisure time availability and an hourly cycle shaped by global time zone distribution. The tables below aggregate all 114 capture events across the 17-day window.'),
            ],
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p('Saturday leads in stream count (1,433 on average) with Sunday close behind (1,326 on average), confirming the expected weekend spike. The midweek pattern (Tuesday through Thursday) is more revealing: stream counts drop to their lowest levels, yet average viewers rise to their highest (33–38). We hypothesize Twitch viewership does not meaningfully decline midweek; rather, fewer streamers go live, so the same audience pool concentrates across a smaller supply of active channels. Thursday is the extreme case, pairing the lowest stream count of any day (879) with the highest average viewers per stream (38). Streamers looking to build their community might consider adding a Thursday stream to their schedule.'),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '1,326', '27'],
                                ['Monday', '1,035', '25'],
                                ['Tuesday', '960', '33'],
                                ['Wednesday', '973', '31'],
                                ['Thursday', '879', '38'],
                                ['Friday', '1,066', '23'],
                                ['Saturday', '1,433', '18'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p('The hourly distribution reveals a primary peak from 19:00 to 01:00 UTC, covering evening hours across Europe and the Americas, with a secondary elevation at 03:00–05:00 UTC reflecting East Asian and late-night North American activity. The trough between 06:00 and 09:00 UTC, when average streams drop below 850, corresponds to overnight hours in the Americas and early morning in Europe.'),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,355', '17'],
                                ['01:00', '1,708', '22'],
                                ['02:00', '1,630', '24'],
                                ['03:00', '1,677', '16'],
                                ['04:00', '1,362', '30'],
                                ['05:00', '1,396', '37'],
                                ['06:00', '831', '21'],
                                ['07:00', '720', '19'],
                                ['08:00', '611', '14'],
                                ['09:00', '641', '19'],
                                ['10:00', '538', '47'],
                                ['11:00', '559', '33'],
                                ['12:00', '673', '23'],
                                ['13:00', '757', '24'],
                                ['14:00', '783', '31'],
                                ['15:00', '860', '23'],
                                ['16:00', '1,038', '21'],
                                ['17:00', '948', '26'],
                                ['18:00', '1,180', '19'],
                                ['19:00', '1,409', '22'],
                                ['20:00', '1,487', '28'],
                                ['21:00', '1,410', '56'],
                                ['22:00', '1,468', '27'],
                                ['23:00', '1,509', '18'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-02-top-party-games-and-streamers.js.map